import React, { useEffect, useState, useRef, useMemo } from "react";
import { Typography, ConfigProvider, Drawer } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchStaff, deleteMerchant } from "../Merchants/store/merchantsUserSlice";
import { useTheme } from "../../context/ThemeContext";
import { getAntdThemeConfig } from "../../utils/antdTheme";
import { ThemeProvider } from "styled-components";
import MerchantBox from "../Merchants/MerchantBox";
import MerchantsTable from "../Merchants/MerchantsTable";
import CustomInput from "../../commonComponents/input";
import { Merchant } from "../Merchants/types";
import notification from "../../commonComponents/Notification/Notification";
import ConfirmDialog from "../../commonComponents/ConfirmDialog";
import {
  Container,
  StyledCard,
  HeaderSection,
  TopControlBar,
  AddButton,
  DrawerContentWrapper,
} from "../Merchants/styles";

const { Title } = Typography;

const Staff: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentStaff, setCurrentStaff] = useState<Merchant | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<Merchant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const { staff, loading } = useSelector(
    (state: RootState) => state.merchants
  );
  const loginData = useSelector((state: RootState) => state.auth.loginData) as { id?: string | number } | null;
  const hasFetchedRef = useRef(false);

  const managerId = loginData?.id != null ? String(loginData.id) : undefined;

  const filteredStaff = useMemo(() => {
    if (!Array.isArray(staff)) return [];
    if (!searchQuery) return staff;
    const query = searchQuery.toLowerCase();
    return staff.filter(
      (staffMember) =>
        staffMember?.name?.toLowerCase().includes(query) ||
        staffMember?.email?.toLowerCase().includes(query)
    );
  }, [staff, searchQuery]);

  const paginatedStaff = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredStaff.slice(startIndex, endIndex);
  }, [filteredStaff, currentPage, pageSize]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchStaff());
    }
  }, [dispatch]);

  const handleAddClick = () => {
    setCurrentStaff(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (staffMember: Merchant) => {
    setCurrentStaff(staffMember);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setCurrentStaff(null);
  };

  const handleDeleteClick = (staffMember: Merchant) => {
    setStaffToDelete(staffMember);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!staffToDelete) return;
    
    setIsDeleting(true);
    const result = await dispatch(
      deleteMerchant({ type: "staff", id: staffToDelete.id })
    );
    setIsDeleting(false);
    
    if (deleteMerchant.fulfilled.match(result)) {
      notification.success("Staff member deleted successfully");
      setIsDeleteModalOpen(false);
      setStaffToDelete(null);
      dispatch(fetchStaff());
    } else {
      const errorMsg =
        typeof result.payload === "string"
          ? result.payload
          : "Error deleting staff member";
      notification.error("Delete Failed", errorMsg);
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setStaffToDelete(null);
    }
  };

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? "dark" : "light" }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <Title level={2} style={{ margin: 0 }}>
              All Staff
            </Title>
            <AddButton
              type="primary"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddClick}
              size="large"
            >
              Add New Staff
            </AddButton>
          </HeaderSection>

          <StyledCard>
            <TopControlBar>
              <CustomInput
                placeholder="Search staff by name or email..."
                value={searchQuery}
                onChange={(value: string) => setSearchQuery(value)}
                allowClear
                width="300px"
              />
            </TopControlBar>

            <MerchantsTable
              merchants={paginatedStaff}
              currentPage={currentPage}
              pageSize={pageSize}
              total={filteredStaff.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              loading={loading.staff}
              showRoleColumn={true}
            />
          </StyledCard>

          <Drawer
            title={currentStaff ? "Edit Staff" : "Add New Staff"}
            placement="right"
            onClose={handleDrawerClose}
            open={isDrawerOpen}
            width={600}
            destroyOnClose
            styles={{
              body: {
                padding: 0,
                background: isDarkMode ? "#1a202c" : "#ffffff",
              },
              header: {
                background: isDarkMode ? "#1a202c" : "#ffffff",
                borderBottom: `1px solid ${isDarkMode ? "#434343" : "#f0f0f0"}`,
              },
            }}
          >
            <DrawerContentWrapper>
              <MerchantBox
                initialMerchant={currentStaff}
                onSave={() => {
                  setIsDrawerOpen(false);
                  setCurrentStaff(null);
                  dispatch(fetchStaff());
                }}
                onCancel={handleDrawerClose}
                fetchMerchants={() => dispatch(fetchStaff())}
                isStaffMode={true}
                managerId={managerId}
              />
            </DrawerContentWrapper>
          </Drawer>

          <ConfirmDialog
            isOpen={isDeleteModalOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Delete Staff Member"
            message={
              staffToDelete
                ? `Are you sure you want to delete "${staffToDelete.name || staffToDelete.email || staffToDelete.username}"? This action cannot be undone.`
                : "Are you sure you want to delete this staff member? This action cannot be undone."
            }
            confirmText="Delete"
            cancelText="Cancel"
            confirmColorScheme="red"
            loading={isDeleting}
          />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default Staff;
